import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, Loader2, Camera, MailCheck, ArrowLeft } from "lucide-react";

function VerifyEmailStep({ email, name, onDone }) {
  const { verifyEmail } = useAuth();
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(60);
  React.useEffect(() => { if (cooldown <= 0) return; const t = setTimeout(() => setCooldown((c) => c - 1), 1000); return () => clearTimeout(t); }, [cooldown]);
  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await verifyEmail(email, otp); toast.success("Email verified"); onDone?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Invalid code"); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    try { await api.post("/auth/send-verification", { email }); setCooldown(60); toast.success("New code sent"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="card-dark p-8" data-testid="organiser-verify-card">
      <div className="chip bg-lime text-ink mb-4">Step 2 · Verify</div>
      <h2 className="font-display uppercase text-3xl md:text-4xl leading-[0.95]">Welcome, {name?.split(" ")[0] || "friend"}.</h2>
      <p className="mt-3 text-sm text-text/70 font-mono">We sent a 6-digit code to <span className="text-lime">{email}</span>. It expires in 5 minutes.</p>
      <form onSubmit={verify} className="mt-6 space-y-3">
        <input required data-testid="organiser-verify-otp" inputMode="numeric" pattern="\d{6}" maxLength={6} placeholder="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} className="input-dark text-center text-2xl tracking-[0.5em] font-display" />
        <button data-testid="organiser-verify-submit" disabled={busy || otp.length !== 6} className="btn-lime w-full justify-center">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" />} {busy ? "Verifying..." : "Verify email"}
        </button>
      </form>
      <div className="mt-4 flex items-center justify-between text-xs font-mono">
        <button onClick={resend} disabled={cooldown > 0} data-testid="organiser-verify-resend" className="text-lime disabled:text-muted underline">{cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}</button>
        <button onClick={onDone} data-testid="organiser-verify-skip" className="text-muted underline">Skip for now →</button>
      </div>
    </div>
  );
}

export default function OrganiserSignup() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", company: "", profile_picture: "" });
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form");

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Image must be under 2MB");
    const r = new FileReader();
    r.onload = () => { setForm((p) => ({ ...p, profile_picture: r.result })); setPreview(r.result); };
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/register-organizer", form);
      // P1.1: no token in response — persist pending verify state
      if (data.token) { localStorage.setItem("eventom_token", data.token); await refresh(); }
      else {
        localStorage.setItem("pending_verify_email", data.user.email);
        localStorage.setItem("pending_verify_name", data.user.name || "");
      }
      toast.success(`Welcome, ${data.user.name}! Verify your email to activate the account.`);
      setStep("verify");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Signup failed"); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-16">
        {step === "form" ? (
          <div className="card-dark p-8">
            <div className="chip bg-pink text-white mb-4">Organiser Signup</div>
            <h1 className="font-display uppercase text-3xl md:text-4xl lg:text-5xl leading-[0.95]">More growth. More tickets. More stories.</h1>
            <p className="mt-2 text-sm text-text/70">Everything you need to launch, grow and run a better event.</p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="flex items-center gap-3">
                <label className="cursor-pointer" data-testid="org-signup-avatar-label">
                  <div className="icon-circle bg-white/5 text-white overflow-hidden !w-16 !h-16 border-2 border-pink/40">
                    {preview ? <img src={preview} alt="" className="w-full h-full object-cover" /> : <Camera className="w-5 h-5" />}
                  </div>
                  <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="org-signup-avatar-input" />
                </label>
                <div className="text-xs text-muted font-mono">Logo/photo (optional)</div>
              </div>
              <input required data-testid="org-signup-name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" />
              <input data-testid="org-signup-company" placeholder="Company / Brand (optional)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input-dark" />
              <input required data-testid="org-signup-email" type="email" placeholder="Business email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark" />
              <input required data-testid="org-signup-phone" type="tel" placeholder="Mobile (+91 9XXXXXXXXX)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-dark" />
              <input required data-testid="org-signup-password" type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-dark" />
              <button data-testid="org-signup-submit" disabled={loading} className="btn-primary w-full justify-center">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Briefcase className="w-4 h-4" />} {loading ? "Creating..." : "Create Organiser Account"}
              </button>
            </form>
            <div className="mt-6 text-sm font-mono text-muted space-y-1">
              <div>Already have an account? <Link to="/login" data-testid="org-signup-goto-login" className="text-lime font-bold underline">Log in</Link></div>
              <div>Just here to attend? <Link to="/signup" data-testid="org-signup-goto-attendee" className="text-lime font-bold underline">Attendee signup →</Link></div>
            </div>
          </div>
        ) : (
          <>
            <button onClick={() => setStep("form")} className="mb-4 flex items-center gap-2 text-xs font-mono uppercase text-muted"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
            <VerifyEmailStep email={form.email} name={form.name} onDone={() => nav("/organizer")} />
          </>
        )}
      </div>
    </Layout>
  );
}
